import { track, useEditor } from 'tldraw'
import { ZoomControls } from '@/components/intueri/ZoomControls'
import { useZoom } from '@/hooks/useZoom'
import { ZOOM_MAX_PERCENT_VECTOR } from '@/constants/zoom'

interface Props {
  /**
   * Camera zoom that reads as 100% — the fit-to-content zoom BoardCanvas last
   * fit to. Supplied by the parent so the readout is calibrated to a stable
   * baseline instead of the live (possibly wheel-zoomed) camera.
   */
  fitBaseline: number
}

/**
 * Board zoom control (FS-562). A thin adapter over the shared <ZoomControls>
 * + `useZoom`:
 *
 *   - Uses the LINEAR ratio model (`'percent'`): the readout is
 *     `round(cameraZoom / fitBaseline * 100)`, so the fit reads 100%, 2× the fit
 *     reads 200%, and half the fit reads 50% — the natural behavior for an
 *     infinite canvas. (The compressed `fit-baseline` model is for P&ID/raster,
 *     where 200% means "2× the document's native pixels"; on the board it made
 *     zoom-out clamp to odd values like 72%.)
 *   - The readout is driven by the LIVE camera zoom (this is a tldraw `track()`
 *     component, so it re-renders on any camera change). That fixes the
 *     "zoom-out number doesn't go down" bug, which came from gating the readout
 *     on a `userHasZoomed` flag that only the +/- buttons set.
 *   - `useZoom` clamps +/- and type-in to the 200% vector cap, and BoardCanvas
 *     bounds the camera's zoomSteps to [25%, 200%] of the fit, so neither the
 *     buttons nor the wheel can reach the old ~3000% readout.
 */
const BoardZoomControlsV2 = track(({ fitBaseline }: Props) => {
  const editor = useEditor()
  const liveZoom = editor?.getZoomLevel() ?? 1

  const { percent, zoomIn, zoomOut, setPercent, canZoomIn, canZoomOut } = useZoom({
    model: 'percent',
    scale: liveZoom,
    baseline: fitBaseline,
    maxPercent: ZOOM_MAX_PERCENT_VECTOR,
    onApply: (nextZ) => {
      if (!editor) return
      const { x, y } = editor.getCamera()
      editor.setCamera({ x, y, z: nextZ }, { animation: { duration: 200 } })
    },
  })

  return (
    <ZoomControls
      placement="bottom-left"
      percent={percent}
      onZoomIn={zoomIn}
      onZoomOut={zoomOut}
      onSetPercent={setPercent}
      canZoomIn={canZoomIn}
      canZoomOut={canZoomOut}
      undoRedo={{
        onUndo: () => editor?.undo(),
        onRedo: () => editor?.redo(),
        // track() re-renders on these reactive signals, so the buttons grey
        // out the moment the undo/redo stacks empty.
        canUndo: editor?.getCanUndo() ?? false,
        canRedo: editor?.getCanRedo() ?? false,
      }}
    />
  )
})

export default BoardZoomControlsV2
